import { Fragment, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useShipAndLanguage } from '@/providers/ship-and-language';
import Badge from '@/components/atoms/badge';
import Link from '@/components/atoms/link';
import StockStatusTag from '@/components/atoms/stock-status-tag';
import VariantSelector from '@/components/molecules/variant-selector';
import { StockStatus } from '@/types/entity/product';
import { resolveStockStatus } from '@/utils/product/resolve-stock-status';
import { resolveQuoteFormUrl } from '@/utils/zendesk-quote-form-url';
import { useTranslations } from 'use-intl';
import { PDPMainInfoProps } from '../types';
import CartCTA from './cart-cta';
import ShoppingListCTA from './shopping-list-cta';
import PricingCard from './pricing-card';

const MainInfo = ({
  product,
  className,
  addToCart,
  getWishlists,
  canUsePurchaseLists = false,
  canAddToOwnWishlist,
  canAddToOthersWishlist,
  addToWishlists,
  removeFromWishlists,
  onChangeSKU,
  isGuest = false,
  isViewOnly = false,
  addToNewWishlist,
  addToCartDisabled,
  backOrdersAllowed = false,
  priceUnavailable = false,
  stockUnavailable = false,
}: PDPMainInfoProps) => {
  const [quantity, setQuantity] = useState(1);

  const { selectedLocation } = useShipAndLanguage();
  const translate = useTranslations();

  // EWM-434 — add-to-cart now surfaces a standard toast instead of the
  // (removed) mini-cart drawer. Toast (success or locked error) is emitted
  // inside usePDPCart.addToCart so the hook owns the full feedback UX.
  const handleOnAddToCart = async (count: number) => {
    await addToCart(count);
  };

  const handleQuantityChange = (count: number) => {
    setQuantity(count);
  };

  const handleRequestQuote = () => {
    const url = resolveQuoteFormUrl(selectedLocation?.value);
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // EWM-481 — binary stock display (aligned with PLP + Cart per product
  // decision): In Stock unless CT enrichment is missing/false. When CT
  // hasn't supplied a stock signal (or said `false`), the strict rule treats
  // the variant as out-of-stock. We never trust Algolia / embedded
  // availability as a fallback.
  // EWM-545 — new/pending products (lifecycle N/P) with no stock read
  // "Stock due soon"; otherwise the existing in/out-of-stock rule applies.
  const stockStatus: StockStatus = resolveStockStatus({
    inStock: product.inStock,
    stockUnavailable,
    isStockDueSoonEligible: product.isStockDueSoonEligible,
  });
  const hasMultipleVariants = (product.variants?.length ?? 0) > 1;

  return (
    <div className={className}>
      <div className="grid gap-6">
        {/* EWM-194 — info-panel header: brand row, H1, SKU/MPN, Pack Size + stock pill, superseded card. */}
        <div className="grid gap-3">
          {(product.brand || product.badge) && (
            <div className="flex items-center gap-3">
              {product.brand && (
                <span className="text-12 font-semibold tracking-wide text-gray-500 uppercase">{product.brand}</span>
              )}
              {product.badge && (
                <Badge variant={product.badge}>
                  {translate(`pdp.badge-${product.badge}` as Parameters<typeof translate>[0])}
                </Badge>
              )}
            </div>
          )}

          <h1 data-testid="product-name" className="text-primary text-28 leading-tight font-bold">
            {product.name}
          </h1>

          <dl className="text-14 flex flex-wrap items-center gap-x-4 gap-y-1 text-gray-600">
            <div className="flex items-center gap-1">
              <dt>{translate('common.cat-no')}:</dt>
              <dd className="font-semibold text-gray-800">{product.sku}</dd>
            </div>
            {product.mpn && (
              <div className="flex items-center gap-1">
                <dt>{translate('pdp.mpn')}:</dt>
                <dd className="font-semibold text-gray-800">{product.mpn}</dd>
              </div>
            )}
          </dl>

          <div className="text-14 flex flex-wrap items-center gap-x-4 gap-y-2 text-gray-600">
            {product.packSize && (
              <div className="flex items-center gap-1">
                <span>{translate('pdp.pack-size')}:</span>
                <span className="font-semibold text-gray-800">{product.packSize}</span>
              </div>
            )}
            {/* EWM-743 — carton quantity sits inline beside "Sold as" in the
                same style. The mapper only sets it for a positive whole number,
                so absent / empty / zero / non-integer all land here as
                undefined and render nothing at all — the flex gap closes up,
                leaving no hole. A carton quantity of 1 is a real value and
                displays normally. Informational only: it constrains neither the
                stepper nor the cart. */}
            {product.cartonQuantity !== undefined && (
              <div className="flex items-center gap-1">
                <span>{translate('pdp.carton-size')}:</span>
                <span className="font-semibold text-gray-800">{product.cartonQuantity}</span>
              </div>
            )}
            <StockStatusTag status={stockStatus} />
          </div>

          {product.supersededBy && product.supersededBy.length > 0 && (
            /* EWM-438 — the callout sits on the white top section next to the
               pricing card. bg-gray-50/100 were too faint to read (QA: "looks
               like just a line"). Use bg-gray-200 + border-gray-300 so it clearly
               stands out as a distinct card, at least as prominent as the pricing
               box (Luke, QA re-test). */
            <div className="rounded-lg border border-gray-300 bg-gray-200 p-4">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0 text-amber-500" aria-hidden />
                <div>
                  <h2 className="text-primary text-14 mb-1 font-bold">{translate('pdp.superseded-title')}</h2>
                  <p className="text-13 text-gray-600">
                    {translate('pdp.superseded-prefix')}{' '}
                    {product.supersededBy.map((replacement, idx) => (
                      <Fragment key={replacement.sku}>
                        {idx > 0 && ', '}
                        <Link href={replacement.url} className="text-primary font-semibold hover:underline">
                          {replacement.name}
                        </Link>
                      </Fragment>
                    ))}
                    {translate('pdp.superseded-suffix')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* EWM-195 — variant selector (multi-variant only) + pricing card. */}
        {hasMultipleVariants && product.sku && product.variants && (
          <VariantSelector
            variants={product.variants}
            currentSku={product.sku}
            currency={product.currency}
            onChange={onChangeSKU}
          />
        )}

        <PricingCard
          product={product}
          isGuest={isGuest}
          priceUnavailable={priceUnavailable}
          onRequestQuote={handleRequestQuote}
        />

        {/* EWM-197 / EWM-653 — cart actions: quantity stepper + Add + Add to
            Purchase List icon button. The list button is Master-only
            (canUsePurchaseLists); View-only and guests see no list action.
            View-only collapses to nothing; guest swaps Add for a login link. */}
        <CartCTA
          product={product}
          countChange={handleQuantityChange}
          addToCart={handleOnAddToCart}
          addToCartDisabled={addToCartDisabled}
          backOrdersAllowed={backOrdersAllowed}
          isViewOnly={isViewOnly}
          isGuest={isGuest}
          addToListButton={
            canUsePurchaseLists ? (
              <ShoppingListCTA
                getWishlists={getWishlists}
                removeFromWishlists={removeFromWishlists}
                addToNewWishlist={(list) => addToNewWishlist(list, quantity)}
                addToWishlists={(wishlistIds) => addToWishlists(wishlistIds, quantity)}
                canAddToOwnWishlist={canAddToOwnWishlist}
                canAddToOthersWishlist={canAddToOthersWishlist}
                // EWM-593 — add-to-purchase-list must stay enabled for
                // out-of-stock products regardless of Back Orders Allowed
                // (it's a bookmark, not a purchase), so do NOT gate on stock /
                // maxQuantity — the PLP tile never gates add-to-list on stock.
                // We keep a price gate here (the PDP add-to-list feeds a priced
                // analytics event); the PLP, which doesn't, is ungated.
                // Add-to-Cart keeps its own out-of-stock / back-order gate.
                addToWishlistDisabled={priceUnavailable || !product.price}
                productName={product.name}
                productSku={product.sku}
              />
            ) : undefined
          }
        />
      </div>
    </div>
  );
};

export default MainInfo;
